import React, { useState, useEffect } from 'react';
import logo2 from '../assets/gp-logo.png';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Check as CheckIcon } from '@mui/icons-material';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRobotVerified, setIsRobotVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  //console.log("API URL:", API_URL); // Debugging line to check the API URL

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleCaptchaVerify = () => {
    if (isRobotVerified) return;
    setIsVerifying(true);
    setCaptchaError('');
    setTimeout(() => {
      setIsVerifying(false);
      setIsRobotVerified(true);
    }, 650);
  };

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Enter a valid email';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!isRobotVerified) {
      setCaptchaError('Please verify that you are not a robot');
    } else {
      setCaptchaError('');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && isRobotVerified;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();


      if (!res.ok) {
        setApiError(data.message || 'Login failed');

      } else {
        // Store token in localStorage
        if (data.token) {
          localStorage.setItem('token', data.token);

          localStorage.setItem("userInfo", JSON.stringify({
            role: data.user.role,
            org_id: data.user.org_id,
            username: data.user.first_name + " " + data.user.last_name,
            profile_pic: data.user.profile_pic,
            organization: data.user.origanisation.org_name,
            organization_logo: data.user.origanisation.logo,
          }));

          // To access later


           setApiError('');
        window.location.reload();

        } else {
          setApiError(data.message || 'Login failed');
        }
       
        // navigate('/admin/dashboard');
      }
    } catch (err) {
      setApiError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <Helmet>
        <title>Admin Login - Glocal Prayer Network</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: '800px',
            height: '800px',
            background: 'radial-gradient(circle, rgba(76, 161, 175, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'pulse 8s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-15%',
            left: '-10%',
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(44, 62, 80, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'pulse 10s ease-in-out infinite reverse',
          },
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 0.8,
            },
            '50%': {
              transform: 'scale(1.1)',
              opacity: 0.5,
            },
          },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            py: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 4, md: 8 },
              width: '100%',
            }}
          >
            {/* Left Side - Logo and Branding */}
            <Box
              sx={{
                flex: { xs: 'none', md: 1 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                maxWidth: { xs: '100%', md: '500px' },
              }}
            >
              {/* Large Logo Display */}
              <Box
                sx={{
                  width: { xs: 200, sm: 280, md: 350 },
                  height: { xs: 200, sm: 280, md: 350 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.2) 0%, rgba(44, 62, 80, 0.2) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '3px solid rgba(76, 161, 175, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 4,
                  boxShadow: '0 0 60px rgba(76, 161, 175, 0.3), inset 0 0 40px rgba(76, 161, 175, 0.1)',
                  position: 'relative',
                  animation: 'glow 3s ease-in-out infinite',
                  '@keyframes glow': {
                    '0%, 100%': {
                      boxShadow: '0 0 60px rgba(76, 161, 175, 0.3), inset 0 0 40px rgba(76, 161, 175, 0.1)',
                    },
                    '50%': {
                      boxShadow: '0 0 80px rgba(76, 161, 175, 0.5), inset 0 0 60px rgba(76, 161, 175, 0.2)',
                    },
                  },
                }}
              >
                <Box
                  onClick={() => window.open('https://glocalprayer.net', '_blank', 'noopener,noreferrer')}
                  title="Visit Glocal Prayer Network Website (glocalprayer.net)"
                  sx={{
                    width: '85%',
                    height: '85%',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 0 25px rgba(76, 161, 175, 0.6)',
                    },
                  }}
                >
                  <img
                    src="https://glocalprayer.net/prayer/public/assets/img/gp-logo.png"
                    alt="Glocal Prayer Network"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Box>

              {/* Branding Text */}
              <Typography
                variant="h3"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 800,
                  mb: 2,
                  letterSpacing: '-1px',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                Glocal Prayer Network
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 400,
                  mb: 1,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                Pray Without Ceasing
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  maxWidth: '400px',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                Offering Hope & Life
              </Typography> */}
            </Box>
            {/* Right Side - Login Form */}
            <Box
              sx={{
                flex: { xs: 'none', md: 1 },
                width: '100%',
                maxWidth: '480px',
              }}
            >
              <Paper
                elevation={0}
                className="gpn-fade-in"
                sx={{
                  padding: { xs: 3, sm: 4, md: 5 },
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.95) 0%, rgba(28, 40, 51, 0.95) 100%)',
                  backdropFilter: 'blur(30px)',
                  border: '1px solid rgba(76, 161, 175, 0.3)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  width: '100%',
                }}
              >
                {/* Form Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 700,
                      mb: 1,
                      letterSpacing: '-0.5px',
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                    }}
                  >
                    Admin Portal
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    Sign in to continue
                  </Typography>
                </Box>

                {/* Error Alert */}
                {apiError && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#FCA5A5',
                      '& .MuiAlert-icon': {
                        color: '#EF4444',
                      },
                    }}
                  >
                    {apiError}
                  </Alert>
                )}

                {/* Login Form */}
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    margin="normal"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: '#FFFFFF',
                        background: 'rgba(255, 255, 255, 0.05)',
                        '& fieldset': {
                          borderColor: 'rgba(76, 161, 175, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(76, 161, 175, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4CA1AF',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#FCA5A5',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    inputProps={{ "data-testid": "password-input" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        color: '#FFFFFF',
                        background: 'rgba(255, 255, 255, 0.05)',
                        '& fieldset': {
                          borderColor: 'rgba(76, 161, 175, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(76, 161, 175, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4CA1AF',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#FCA5A5',
                      },
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Typography
                      variant="body2"
                      component={Link}
                      to="/forgot-password"
                      sx={{
                        color: '#4CA1AF',
                        textDecoration: 'none',
                        fontWeight: 500,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        '&:hover': {
                          color: '#5FB1C1',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Forgot Password?
                    </Typography>
                  </Box>

                  {/* I'm not a robot Captcha */}
                  <Paper
                    elevation={0}
                    onClick={handleCaptchaVerify}
                    sx={{
                      mb: 2.5,
                      p: 1.5,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: isRobotVerified
                        ? '1px solid rgba(76, 161, 175, 0.6)'
                        : (captchaError ? '1px solid #EF4444' : '1px solid rgba(255, 255, 255, 0.15)'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: isVerifying ? 'wait' : 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.07)',
                        borderColor: isRobotVerified ? '#4CA1AF' : 'rgba(76, 161, 175, 0.4)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          border: isRobotVerified ? 'none' : '2px solid rgba(255, 255, 255, 0.4)',
                          background: isRobotVerified ? '#4CA1AF' : 'rgba(255, 255, 255, 0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {isVerifying ? (
                          <CircularProgress size={16} sx={{ color: '#4CA1AF' }} />
                        ) : isRobotVerified ? (
                          <CheckIcon sx={{ color: '#FFFFFF', fontSize: 18 }} />
                        ) : null}
                      </Box>
                      <Typography
                        sx={{
                          color: isRobotVerified ? '#FFFFFF' : 'rgba(255, 255, 255, 0.85)',
                          fontWeight: isRobotVerified ? 600 : 500,
                          fontSize: '0.9rem',
                          userSelect: 'none',
                        }}
                      >
                        I'm not a robot
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.8 }}>
                      <img
                        src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
                        alt="reCAPTCHA"
                        style={{ width: 24, height: 24, objectFit: 'contain' }}
                      />
                      <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255, 255, 255, 0.5)', lineHeight: 1 }}>
                        reCAPTCHA
                      </Typography>
                    </Box>
                  </Paper>
                  {captchaError && (
                    <Typography sx={{ color: '#FCA5A5', fontSize: '0.75rem', mt: -1.8, mb: 2, ml: 0.5 }}>
                      {captchaError}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.75,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                      boxShadow: '0 4px 20px rgba(76, 161, 175, 0.4)',
                      border: '1px solid rgba(76, 161, 175, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                        boxShadow: '0 6px 30px rgba(76, 161, 175, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </Box>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.75rem',
                    }}
                  >
                    ┬⌐ 2024 Glocal Prayer Network. All rights reserved.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Login;
